import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import { isForbiddenError } from "#app/lib/utils/is-forbidden-error";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  ActivityViewDataDocument,
  CreateNoteDocument,
  DeleteNoteDocument,
  ToggleNoteReactionDocument,
  UpdateNoteDocument,
  type CreateNoteInput,
} from "#app/lib/data";
import {
  addNoteToCache,
  addReplyToCache,
  adjustIssueNoteCount,
  removeNoteFromCache,
} from "./note-cache-utils";
import { useAnalytics, AnalyticsContext, NoteEvent } from "#app/lib/analytics";

// ═══════════════════════════════════════════════════════════════════════════════
// Note Mutations Hook
// ═══════════════════════════════════════════════════════════════════════════════
// Wraps all note CRUD + reaction mutations with Apollo cache updates.
// apiv2's mutations return Note directly. The server writes the corresponding
// activity row, so we refetch ActivityViewData (the feed query used by
// useActivityViewData)
// to show the new "note" activity entry — activity-list only renders
// notes that are referenced by an activity.

export function useNoteMutations(issueId: string) {
  const { track } = useAnalytics();
  const [createNoteMutation] = useMutation(CreateNoteDocument);
  const [updateNoteMutation] = useMutation(UpdateNoteDocument);
  const [deleteNoteMutation] = useMutation(DeleteNoteDocument);
  const [toggleReactionMutation] = useMutation(ToggleNoteReactionDocument);

  const createNote = useCallback(
    async (input: CreateNoteInput) => {
      try {
        const { data, error } = await createNoteMutation({
          variables: { input },
          refetchQueries: [ActivityViewDataDocument],
          update(cache, { data }) {
            const note = data?.createNote;
            if (!note) return;
            if (input.parentNoteId) {
              addReplyToCache(cache, input.parentNoteId, note);
            } else {
              addNoteToCache(cache, issueId, note);
              adjustIssueNoteCount(cache, issueId, 1);
            }
          },
        });
        if (error) {
          if (!isForbiddenError(error)) {
            toastManager.add({ title: "Failed to add comment", type: "error" });
          }
          return null;
        }
        if (data?.createNote) {
          track({ context: AnalyticsContext.Note, event: NoteEvent.Create, label: input.parentNoteId ? "reply" : "note" });
          toastManager.add({
            title: input.parentNoteId ? "Reply added" : "Comment added",
            type: "success",
          });
          return data.createNote;
        }
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to add comment");
        return null;
      }
    },
    [createNoteMutation, issueId, track],
  );

  const updateNote = useCallback(
    async (noteId: string, content: string, mediaIds?: string[] | null) => {
      try {
        const { data, error } = await updateNoteMutation({
          variables: { id: noteId, note: content, mediaIds },
          refetchQueries: [ActivityViewDataDocument],
          // Apollo's normalized cache auto-merges the returned Note onto
          // the existing entry — no explicit cache update needed.
        });
        if (error) {
          if (!isForbiddenError(error)) {
            toastManager.add({ title: "Failed to update comment", type: "error" });
          }
          return null;
        }
        if (data?.updateNote) {
          track({ context: AnalyticsContext.Note, event: NoteEvent.Update });
          toastManager.add({ title: "Comment updated", type: "success" });
          return data.updateNote;
        }
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to update comment");
        return null;
      }
    },
    [updateNoteMutation, track],
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      try {
        const { data, error } = await deleteNoteMutation({
          variables: { id: noteId },
          refetchQueries: [ActivityViewDataDocument],
          update(cache, { data }) {
            const note = data?.deleteNote;
            if (!note?.noteId) return;
            removeNoteFromCache(cache, issueId, note.noteId);
            // The card's count is of top-level comments; a reply is not one.
            if (!note.parentNoteId) adjustIssueNoteCount(cache, issueId, -1);
            // `identify` answers `undefined` for an object it cannot key, and
            // the cache reads an absent `id` as the ROOT — the eviction would
            // drop a ROOT_QUERY field instead of this note.
            const cacheId = cache.identify(note);
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        // Apollo errorPolicy: "all" puts top-level errors in the
        // result `error` instead of throwing — the global error link
        // already toasts for forbidden codes, skip the optimistic
        // "Comment deleted" success in that case.
        if (error) {
          if (!isForbiddenError(error)) {
            toastManager.add({ title: "Failed to delete comment", type: "error" });
          }
          return;
        }
        if (!data?.deleteNote) {
          toastManager.add({ title: "Failed to delete comment", type: "error" });
          return;
        }
        track({ context: AnalyticsContext.Note, event: NoteEvent.Delete });
        toastManager.add({ title: "Comment deleted", type: "success" });
        return data;
      } catch (err) {
        toastMutationError(err, "Failed to delete comment");
      }
    },
    [deleteNoteMutation, issueId, track],
  );

  const toggleReaction = useCallback(
    async (noteId: string, emoji: string) => {
      // The mutation returns the full updated note — Apollo auto-merges it
      await toggleReactionMutation({
        variables: { noteId, emoji },
      });
      track({ context: AnalyticsContext.Note, event: NoteEvent.React, data: { emoji } });
    },
    [toggleReactionMutation, track],
  );

  return { createNote, updateNote, deleteNote, toggleReaction };
}
